import { useMemo, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { ClickArea, Convert, useLoading } from '@revenuemarket/react';
import { Toast, Typography } from '@revenuemarket/ui';
import { assert, convert } from '@revenuemarket/utils';
import { mixpanelTrack } from '@revenuemarket/utils/mixpanel';
import { Button, Drawer, Form, Input } from 'antd';
import { Rule } from 'antd/es/form';
import forge from 'node-forge';
import { match } from 'ts-pattern';
import * as yup from 'yup';

import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { ConnectedCodefCertificate } from '~models/certificate';
import {
  extractP12Certificate,
  extractP12PrivateCertificate,
  parsePEMCertificate,
} from '~utils/certificate/pfx';
import { getFilesFromLocal } from '~utils/file/getFilesFromLocal';
import { validatePfxFile } from '~utils/file/validator';
import { ParsedCertificateDetails } from '~utils/override/forge/types';

interface CertificateLoginForm {
  password: string;
}

const LoginScheme = yup.object({
  password: yup.string().required('비밀번호를 입력해주세요'),
});

const getSchemeRule = (field: keyof yup.InferType<typeof LoginScheme>): Rule => ({
  validator: async (_, value) => {
    await LoginScheme.validateAt(field, { [field]: value });
  },
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onFinish: (certificate: ConnectedCodefCertificate) => void;
}

const CertificateFileUploadDrawer = ({ isOpen, onClose, onFinish: onFinishProp }: Props) => {
  const [certificate, setCertificate] = useState<{ encoded: string; extracted?: string }>();
  const parsedCertificate = useMemo<ParsedCertificateDetails | undefined>(
    () => convert(certificate?.extracted, parsePEMCertificate),
    [certificate],
  );

  const [form] = Form.useForm<CertificateLoginForm>();
  const [isLoading, runTransaction] = useLoading();

  const getCertificateType = (parsed: ParsedCertificateDetails) => {
    return match(parsed.organizationUnit)
      .with('corporation', () => '은행기업')
      .with('personal4IB', () => '은행개인')
      .otherwise(() => '은행개인');
  };

  const onFinish = (values: CertificateLoginForm) => {
    try {
      mixpanelTrack(MIXPANEL_EVENT.CERTIFICATE.CLICK_REGISTER_CERTIFICATE);
      assert(certificate != null);
      const privateCertificate = extractP12PrivateCertificate(certificate.encoded, values.password);
      const parsed = parsePEMCertificate(privateCertificate.certificate);
      assert(parsed != null);
      if (parsed.expireStatus === 'EXPIRED') {
        Toast.error('만료된 공동인증서입니다. 다른 공동인증서를 선택해주세요');
        setCertificate(undefined);
        return;
      }

      const codefCertificate: ConnectedCodefCertificate = {
        certificateExpireDate: parsed.expireDate,
        certificateOwner: parsed.subjectCN,
        cert: certificate.encoded,
        password: values.password,
        certificateAuthority: parsed.simpleOIDName,
        certificateType: match(parsed.organizationUnit)
          .with('corporation', () => '은행기업')
          .with('personal4IB', () => '은행개인')
          .otherwise(() => '은행개인'),
      };
      runTransaction(() => Promise.resolve(onFinishProp(codefCertificate)));
    } catch {
      Toast.error('공동인증서 비밀번호가 올바르지 않습니다.');
    }
  };

  const uploadCertificate = async () => {
    const files = await getFilesFromLocal({
      accept: ['application/x-pkcs12'],
      multiple: false,
    });

    if (files != null) {
      const [file] = files;
      if (!(await validatePfxFile(file))) {
        Toast.error('.pfx, .p12 파일만 업로드 가능합니다.');
        return;
      }
      const buffer = await file.arrayBuffer();
      const encoded = forge.util.binary.base64.encode(new Uint8Array(buffer));

      try {
        const certificate = extractP12Certificate(encoded);
        setCertificate({ encoded, extracted: certificate });
      } catch {
        setCertificate({ encoded });
      }
    }
  };

  return (
    <Drawer open={isOpen} onClose={onClose} destroyOnClose title="공동인증서 업로드">
      <Convert
        value={certificate}
        expression={() => (
          <Flex direction="column" gap={16}>
            <Flex
              direction="column"
              gap={8}
              className={css`
                width: 100%;
                padding: 16px;
                border: 1px solid ${theme.colorTokens.borderDefault};
                border-radius: 6px;
              `}
            >
              <Convert
                value={parsedCertificate}
                expression={(parsedCertificate) => (
                  <>
                    <Typography variant="body1" useEllipsis color={theme.colorTokens.textDefault}>
                      {parsedCertificate.subjectCN}
                    </Typography>
                    <Flex direction="column" gap={4}>
                      <Typography variant="caption1" color={theme.colorTokens.textDescription}>
                        {parsedCertificate.simpleOIDName}
                        {' · '}
                        {getCertificateType(parsedCertificate)}
                      </Typography>
                      <Typography variant="caption1" color={theme.colorTokens.textDescription}>
                        만료일 {parsedCertificate.expireDate}
                      </Typography>
                    </Flex>
                  </>
                )}
                defaultValue={
                  <Typography variant="body1" useEllipsis color={theme.colorTokens.textDefault}>
                    암호화된 인증서는 정보를 미리볼 수 없습니다.
                    <br />
                    공동인증서 등록 후 인증서 정보를 확인해주세요.
                  </Typography>
                }
              />
            </Flex>
            <Form form={form} onFinish={onFinish}>
              <Form.Item name="password" rules={[getSchemeRule('password')]}>
                <Input size="large" type="password" placeholder="공동인증서 비밀번호" />
              </Form.Item>
              <Form.Item<CertificateLoginForm> dependencies={['password']}>
                {({ getFieldsValue }) => {
                  const valid = (() => {
                    try {
                      LoginScheme.validateSync(getFieldsValue());
                      return true;
                    } catch {
                      return false;
                    }
                  })();

                  return (
                    <Button
                      type="primary"
                      size="large"
                      htmlType="submit"
                      disabled={!valid}
                      loading={isLoading}
                      className={css`
                        width: 100%;
                      `}
                    >
                      공동인증서 등록
                    </Button>
                  );
                }}
              </Form.Item>
            </Form>
          </Flex>
        )}
        defaultValue={
          <Flex direction="column" gap={16}>
            <ClickArea onClick={uploadCertificate}>
              <Flex
                justify="center"
                align="center"
                className={css`
                  width: 100%;
                  padding: 16px;
                  background-color: ${theme.colorTokens.bgWeak};
                  border: 1px dashed ${theme.colorTokens.borderDefault};
                  border-radius: 6px;
                `}
              >
                <Typography variant="body1" color={theme.colorTokens.textDescription}>
                  은행에 등록된 공동인증서(.pfx, .p12 확장자)를 업로드해주세요.
                </Typography>
              </Flex>
            </ClickArea>
            <Button onClick={uploadCertificate}>업로드하기</Button>
          </Flex>
        }
      />
    </Drawer>
  );
};

export default CertificateFileUploadDrawer;
